// The TikTok Direct-Post options UX, shared by the single-post composer and the
// folder-import batch panel. TikTok's "Required UX Implementation in Your App"
// (Content Sharing Guidelines → Direct Post API) demands the same controls in
// every surface that posts on the creator's behalf, so both flows render this
// one component rather than each growing its own half of the rules.
//
// In the batch flow the creator makes ONE explicit choice that applies to every
// TikTok post in the import — still a manual selection, just not 37 of them.

import type { TikTokCreatorInfo, TikTokOptions, TikTokPrivacyLevel } from "../api";

/** Which flow the panel is rendered in — only affects the wording. */
export type TikTokScope = "single" | "batch";

/** The creator's choices as the UI holds them. Deliberately NOT `TikTokOptions`:
 * the UI must be able to represent "nothing chosen yet" (`privacyLevel: ""`),
 * which the wire shape cannot. */
export interface TikTokSettings {
  privacyLevel: TikTokPrivacyLevel | "";
  allowComment: boolean;
  allowDuet: boolean;
  allowStitch: boolean;
  disclose: boolean;
  yourBrand: boolean;
  brandedContent: boolean;
}

/** The only legal starting point: no privacy preselected (§2b) and every
 * interaction off (§2c). Both flows seed their state from this. */
export const EMPTY_TIKTOK_SETTINGS: TikTokSettings = {
  privacyLevel: "",
  allowComment: false,
  allowDuet: false,
  allowStitch: false,
  disclose: false,
  yourBrand: false,
  brandedContent: false,
};

// Human labels for TikTok's privacy levels (the API returns raw enum strings).
const TIKTOK_PRIVACY_LABELS: Record<string, string> = {
  PUBLIC_TO_EVERYONE: "Everyone",
  MUTUAL_FOLLOW_FRIENDS: "Friends",
  FOLLOWER_OF_CREATOR: "Followers",
  SELF_ONLY: "Only me",
};

const BRANDED_CONTENT_POLICY = "https://www.tiktok.com/legal/page/global/bc-policy/en";
const MUSIC_USAGE_CONFIRMATION =
  "https://www.tiktok.com/legal/page/global/music-usage-confirmation/en";

// Singular vs batch-wide phrasing. The strings differ only here so the two flows
// can't drift apart in meaning.
const COPY: Record<TikTokScope, { privacyLabel: string; privacyPlaceholder: string }> = {
  single: {
    privacyLabel: "Who can view this video",
    privacyPlaceholder: "Select who can view this video",
  },
  batch: {
    privacyLabel: "Who can view these videos",
    privacyPlaceholder: "Select who can view these videos",
  },
};

/** Build the options object the backend expects, honouring account flags: a
 * `*_disabled` flag from creator_info forces that interaction off regardless of
 * the checkbox, so a greyed-out control can never leak an "allowed" value. */
export function buildTikTokOptions(
  settings: TikTokSettings,
  info: TikTokCreatorInfo | null,
): TikTokOptions {
  return {
    privacyLevel: (settings.privacyLevel || "SELF_ONLY") as TikTokPrivacyLevel,
    disableComment: info?.commentDisabled ? true : !settings.allowComment,
    disableDuet: info?.duetDisabled ? true : !settings.allowDuet,
    disableStitch: info?.stitchDisabled ? true : !settings.allowStitch,
    commercialContent: settings.disclose,
    yourBrand: settings.disclose && settings.yourBrand,
    brandedContent: settings.disclose && settings.brandedContent,
  };
}

/** The account's maximum post length in seconds, or `null` when TikTok didn't
 * give us a usable one.
 *
 * `creator_info` may omit `max_video_post_duration_sec` entirely, and the
 * adapter defaults the missing value to 0. A non-positive limit means "unknown",
 * NOT "every clip is too long" — so it must read as "no limit" to previews and
 * gates alike. This is the ONE place that rule lives: every caller (the §5a
 * previews and the §1c gates, single-post and batch) takes its limit from here,
 * so the flag shown on a row and the gate on the button can never disagree. */
export function tiktokMaxDurationSec(info: TikTokCreatorInfo | null): number | null {
  const limit = info?.maxVideoPostDurationSec ?? 0;
  return limit > 0 ? limit : null;
}

/** What (if anything) about the creator's choices blocks publishing. Shared so
 * the batch gate and the single-post gate enforce exactly the same rules. */
export function tiktokSettingsBlocker(
  settings: TikTokSettings,
  scope: TikTokScope,
): string | null {
  if (!settings.privacyLevel) return `${COPY[scope].privacyPlaceholder}.`;
  if (settings.disclose && !settings.yourBrand && !settings.brandedContent) {
    return "You need to indicate if your content promotes yourself, a third party, or both.";
  }
  if (settings.disclose && settings.brandedContent && settings.privacyLevel === "SELF_ONLY") {
    return "Branded content visibility cannot be set to private.";
  }
  return null;
}

export function TikTokPanel({
  info,
  scope,
  value,
  onChange,
  disabled = false,
}: {
  info: TikTokCreatorInfo;
  scope: TikTokScope;
  value: TikTokSettings;
  onChange: (next: TikTokSettings) => void;
  /** Freezes every control while a batch is mid-flight. */
  disabled?: boolean;
}) {
  const copy = COPY[scope];
  const set = (patch: Partial<TikTokSettings>) => onChange({ ...value, ...patch });

  // Branded content cannot be posted privately — hide SELF_ONLY while it's on.
  const privacyOptions = info.privacyLevelOptions.filter(
    (p) => !(value.disclose && value.brandedContent && p === "SELF_ONLY"),
  );

  return (
    <div className="card tiktok-panel">
      <h2>TikTok post options</h2>
      {scope === "batch" && (
        <p className="muted small">These apply to every TikTok post in this batch.</p>
      )}

      {/* §1a — the creator must be able to see which account will receive the post. */}
      <div className="tiktok-creator">
        {info.creatorAvatarUrl && (
          <img
            className="tiktok-avatar"
            src={info.creatorAvatarUrl}
            alt=""
            referrerPolicy="no-referrer"
          />
        )}
        <div className="conn-body">
          <div className="conn-name">{info.creatorNickname || "TikTok creator"}</div>
          {info.creatorUsername && <div className="muted small">@{info.creatorUsername}</div>}
        </div>
      </div>

      <label className="field">
        <span>{copy.privacyLabel}</span>
        {/* §2b — options come only from `privacy_level_options`, and no option is
            pre-selected: the creator must choose. */}
        <select
          value={value.privacyLevel}
          disabled={disabled}
          onChange={(e) => set({ privacyLevel: e.target.value as TikTokPrivacyLevel | "" })}
        >
          <option value="" disabled>
            {copy.privacyPlaceholder}
          </option>
          {privacyOptions.map((p) => (
            <option key={p} value={p}>
              {TIKTOK_PRIVACY_LABELS[p] ?? p}
            </option>
          ))}
        </select>
      </label>

      {/* §2c — none checked by default; an account-level disabled flag greys the
          control out and forces it off. */}
      <div className="field">
        <span>Allow users to</span>
        <div className="tiktok-toggles">
          <label className="tiktok-check">
            <input
              type="checkbox"
              checked={info.commentDisabled ? false : value.allowComment}
              disabled={disabled || info.commentDisabled}
              onChange={(e) => set({ allowComment: e.target.checked })}
            />
            Comment
          </label>
          <label className="tiktok-check">
            <input
              type="checkbox"
              checked={info.duetDisabled ? false : value.allowDuet}
              disabled={disabled || info.duetDisabled}
              onChange={(e) => set({ allowDuet: e.target.checked })}
            />
            Duet
          </label>
          <label className="tiktok-check">
            <input
              type="checkbox"
              checked={info.stitchDisabled ? false : value.allowStitch}
              disabled={disabled || info.stitchDisabled}
              onChange={(e) => set({ allowStitch: e.target.checked })}
            />
            Stitch
          </label>
        </div>
      </div>

      {/* §3 — commercial-content disclosure, off by default. */}
      <div className="field">
        <label className="tiktok-check">
          <input
            type="checkbox"
            checked={value.disclose}
            disabled={disabled}
            onChange={(e) => set({ disclose: e.target.checked })}
          />
          Disclose video content
        </label>
        <p className="muted small tiktok-hint">
          Turn on if this post promotes a brand, product, or service (yours or someone else's).
        </p>
        {value.disclose && (
          <div className="tiktok-toggles" style={{ marginTop: 8 }}>
            <label className="tiktok-check">
              <input
                type="checkbox"
                checked={value.yourBrand}
                disabled={disabled}
                onChange={(e) => set({ yourBrand: e.target.checked })}
              />
              <span>
                Your brand
                <span className="muted small tiktok-hint"> — promoting your own brand or business</span>
              </span>
            </label>
            <label className="tiktok-check">
              <input
                type="checkbox"
                checked={value.brandedContent}
                disabled={disabled}
                onChange={(e) => {
                  const brandedContent = e.target.checked;
                  set({
                    brandedContent,
                    // Branded content can't be private — clear a now-invalid
                    // SELF_ONLY choice so the picker matches the options shown.
                    ...(brandedContent && value.privacyLevel === "SELF_ONLY"
                      ? { privacyLevel: "" as const }
                      : {}),
                  });
                }}
              />
              <span>
                Branded content
                <span className="muted small tiktok-hint"> — a paid partnership with another brand</span>
              </span>
            </label>
          </div>
        )}
      </div>

      {/* Music Usage Confirmation declaration (audit rule) — wording depends on
          whether branded content is disclosed. */}
      <p className="muted small">
        {value.disclose && value.brandedContent ? (
          <>
            By posting, you agree to TikTok's{" "}
            <a href={BRANDED_CONTENT_POLICY} target="_blank" rel="noreferrer">
              Branded Content Policy
            </a>{" "}
            and{" "}
            <a href={MUSIC_USAGE_CONFIRMATION} target="_blank" rel="noreferrer">
              Music Usage Confirmation
            </a>
            .
          </>
        ) : (
          <>
            By posting, you agree to TikTok's{" "}
            <a href={MUSIC_USAGE_CONFIRMATION} target="_blank" rel="noreferrer">
              Music Usage Confirmation
            </a>
            .
          </>
        )}
      </p>
    </div>
  );
}
